import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Mail, ExternalLink } from "lucide-react";

export function ContactMain() {
  const formMobile = useRef(null);
  const formTablet = useRef(null);
  const formDesktop = useRef(null);

  const [status, setStatus] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);

  const sendEmail = (e, formRef) => {
    e.preventDefault();
    if (!formRef.current) return;

    setStatus("loading");
    setIsDisabled(true);

    emailjs
      .sendForm(
        "service_kigu4gr",
        "template_7of2pod",
        formRef.current,
        "R0SQ-HydqmW-zaHsu"
      )
      .then(
        () => {
          setStatus("success");
          formRef.current.reset();
          setTimeout(() => {
            setStatus("");
            setIsDisabled(false);
          }, 3000);
        },
        () => {
          setStatus("error");
          setTimeout(() => {
            setStatus("");
            setIsDisabled(false);
          }, 3000);
        }
      );
  };

  return (
    <section
      id="contact"
      className="relative w-full h-full flex flex-col justify-center items-center px-6 overflow-hidden"
    >

      {/* MOBILE (< md) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-md mx-auto flex flex-col gap-6 items-center md:hidden my-[100px]"
      >
        <div className="flex flex-col justify-center gap-4 text-center px-1">
          <div className="relative inline-block isolate mx-auto">
            <h2 className="relative z-10 text-3xl font-extrabold bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(99,102,241,0.4)] leading-tight">
              Need Support?
            </h2>
          </div>

          <p className="text-gray-400 text-[14px] leading-relaxed">
            Facing a technical issue or have feedback for us?
            <br /> Our support team will make sure you are taken care of.
          </p>

          <div className="flex flex-col gap-6 text-gray-300 mt-4">
            <div className="flex items-start justify-center gap-10">
              <div className="flex flex-col items-center min-w-[110px]">
                <div className="flex items-center gap-2">
                  <Mail size={20} className="text-indigo-400" />
                  <span className="font-semibold text-white text-sm">Email</span>
                </div>
                <p className="text-gray-400 text-xs text-center mt-1">
                  support@floatkeygames.com
                </p>
              </div>

              <div className="flex flex-col items-center min-w-[110px]">
                <div className="flex items-center gap-2">
                  <ExternalLink size={20} className="text-indigo-400" />
                  <span className="font-semibold text-white text-sm">
                    Website
                  </span>
                </div>
                <p className="text-gray-400 text-xs text-center mt-1">
                  www.floatkeygames.com
                </p>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          ref={formMobile}
          onSubmit={(e) => sendEmail(e, formMobile)}
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col gap-4 w-full max-w-sm mx-auto p-4"
        >
          <input
            type="text"
            name="user_name"
            placeholder="Your name"
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <input
            type="email"
            name="user_email"
            placeholder="you@example.com"
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <textarea
            name="message"
            rows={4}
            placeholder="Your message..."
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px] resize-none"
          />

          <motion.button
            type="submit"
            disabled={isDisabled}
            whileHover={{ scale: status ? 1 : 1.05 }}
            whileTap={{ scale: status ? 1 : 0.97 }}
            className={`mt-1 px-6 py-3 rounded-xl font-semibold text-white flex items-center justify-center transition-all duration-300 ${
              status === "loading"
                ? "bg-white/10 cursor-wait"
                : status === "success"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500"
                : status === "error"
                ? "bg-red-700"
                : "bg-gradient-to-r from-[#131313] to-[#131313] hover:from-indigo-500 hover:to-purple-500"
            }`}
          >
            {status === "loading" && (
              <motion.div
                className="h-5 w-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
                transition={{ duration: 0.5 }}
              />
            )}
            {status === "success" && "Email sent successfully! ✅"}
            {status === "error" && "Failed to send ❌"}
            {status === "" && "Send Message"}
          </motion.button>
        </motion.form>
      </motion.div>

      {/* TABLET (md ≤ width < lg) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-3xl mx-auto hidden md:grid lg:hidden md:grid-cols-2 gap-8 items-start my-[90px] px-[10px]"
      >
        <div className="flex flex-col justify-center gap-4 text-center md:text-left px-2">
          <div className="relative inline-block isolate mx-auto md:mx-0">
            <h2 className="relative z-10 pt-[20px] text-4xl font-extrabold bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(99,102,241,0.4)] leading-tight">
              Need Support?
            </h2>
          </div>

          <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto md:mx-0">
            Facing a technical issue or have feedback for us?
            <br /> Our support team will make sure you are taken care of.
          </p>

          <div className="flex flex-col gap-4 text-gray-300 mt-4">
            <div className="flex items-center justify-center md:justify-start gap-3">
              <Mail size={22} className="text-indigo-400" />
              <div>
                <div className="font-semibold text-white text-sm">Email</div>
                <div className="text-gray-400 text-xs">
                  support@floatkeygames.com
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center md:justify-start gap-3">
              <ExternalLink size={22} className="text-indigo-400" />
              <div>
                <div className="font-semibold text-white text-sm">Website</div>
                <div className="text-gray-400 text-xs">
                  www.floatkeygames.com
                </div>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          ref={formTablet}
          onSubmit={(e) => sendEmail(e, formTablet)}
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex flex-col gap-4 p-6"
        >
          <input
            type="text"
            name="user_name"
            placeholder="Your name"
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <input
            type="email"
            name="user_email"
            placeholder="you@example.com"
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <textarea
            name="message"
            rows={4}
            placeholder="Your message..."
            required
            disabled={isDisabled}
            className="w-full p-3 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px] resize-none"
          />

          <motion.button
            type="submit"
            disabled={isDisabled}
            whileHover={{ scale: status ? 1 : 1.05 }}
            whileTap={{ scale: status ? 1 : 0.97 }}
            className={`mt-1 px-6 py-3 rounded-xl font-semibold text-white flex items-center justify-center transition-all duration-300 ${
              status === "loading"
                ? "bg-white/10 cursor-wait"
                : status === "success"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500"
                : status === "error"
                ? "bg-red-700"
                : "bg-gradient-to-r from-[#131313] to-[#131313] hover:from-indigo-500 hover:to-purple-500"
            }`}
          >
            {status === "loading" && (
              <motion.div
                className="h-5 w-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
                transition={{ duration: 0.5 }}
              />
            )}
            {status === "success" && "Email sent successfully! ✅"}
            {status === "error" && "Failed to send ❌"}
            {status === "" && "Send Message"}
          </motion.button>
        </motion.form>
      </motion.div>

      {/* PC (≥ lg) */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 w-full max-w-5xl mx-auto hidden lg:grid lg:grid-cols-2 gap-12 items-start mt-[10px] px-[40px]"
      >
        <div className="flex flex-col justify-center gap-5 text-center lg:text-left mt-8">
          <div className="relative inline-block isolate mx-auto lg:mx-0">
            <h2 className="relative z-10 text-4xl lg:text-5xl font-extrabold bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(99,102,241,0.4)] leading-tight">
              Need Support?
            </h2>
          </div>

          <p className="text-gray-400 max-w-md mx-auto lg:mx-0 text-base leading-relaxed">
            Facing a technical issue or have feedback for us?
            <br /> Our support team will make sure you are taken care of.
          </p>

          <div className="flex flex-col gap-4 text-gray-300 mt-4">
            <div className="flex items-center justify-center lg:justify-start gap-3">
              <Mail size={22} className="text-indigo-400" />
              <div>
                <div className="font-semibold text-white">Email</div>
                <div className="text-gray-400 text-sm">
                  support@floatkeygames.com
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center lg:justify-start gap-3">
              <ExternalLink size={22} className="text-indigo-400" />
              <div>
                <div className="font-semibold text-white">Website</div>
                <div className="text-gray-400 text-sm">
                  www.floatkeygames.com
                </div>
              </div>
            </div>
          </div>
        </div>

        <motion.form
          ref={formDesktop}
          onSubmit={(e) => sendEmail(e, formDesktop)}
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col gap-5 p-8"
        >
          <input
            type="text"
            name="user_name"
            placeholder="Your name"
            required
            disabled={isDisabled}
            className="w-full p-4 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <input
            type="email"
            name="user_email"
            placeholder="you@example.com"
            required
            disabled={isDisabled}
            className="w-full p-4 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <textarea
            name="message"
            rows={4}
            placeholder="Your message..."
            required
            disabled={isDisabled}
            className="w-full p-4 rounded-xl bg-[#131313] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y min-h-[120px] max-h-[300px]"
          />

          <motion.button
            type="submit"
            disabled={isDisabled}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className={`mt-2 px-6 py-3 rounded-xl font-semibold text-white flex items-center justify-center transition-all duration-300 ${
              status === "loading"
                ? "bg-white/10 cursor-wait"
                : status === "success"
                ? "bg-gradient-to-r from-indigo-500 to-purple-500"
                : status === "error"
                ? "bg-red-700"
                : "bg-gradient-to-r from-[#131313] to-[#131313] hover:from-indigo-500 hover:to-purple-500"
            }`}
          >
            {status === "loading" && (
              <motion.div
                className="h-5 w-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"
                transition={{ duration: 0.5 }}
              />
            )}
            {status === "success" && "Email sent successfully! ✅"}
            {status === "error" && "Failed to send ❌"}
            {status === "" && "Send Message"}
          </motion.button>
        </motion.form>
      </motion.div>
    </section>
  );
}

export default ContactMain;
